import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Paper,
  Grid,
  Avatar,
  TextField,
  Button,
  Divider,
  Card,
  CardContent,
  Tab,
  Tabs,
  IconButton,
  CircularProgress,
  Tooltip,
} from "@mui/material";
import {
  Edit as EditIcon,
  Save as SaveIcon,
  PhotoCamera as PhotoCameraIcon,
  Lock as LockIcon,
  Person as PersonIcon,
  Badge as BadgeIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  QrCode as QrCodeIcon,
} from "@mui/icons-material";
import { useAuth } from "../../context/AuthContext";
import { usersAPI, gateAPI } from "../../services/api";
import { toast } from "react-toastify";
import { getErrorMessage } from "../../utils/helpers";

const Profile = () => {
  const { user, updateUser } = useAuth();
  const [activeTab, setActiveTab] = useState(0);
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [qrCodeData, setQrCodeData] = useState(null);
  const [qrLoading, setQrLoading] = useState(false);

  // Form states
  const [profileData, setProfileData] = useState({
    name: "",
    email: "",
    phoneNumber: "",
    studentId: "",
    address: {
      street: "",
      city: "",
      state: "",
      pincode: "",
      country: "India",
    },
    emergencyContact: {
      name: "",
      phone: "",
      relation: "",
    },
    aadharNumber: "",
  });

  const [passwordData, setPasswordData] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });

  useEffect(() => {
    if (user) {
      setProfileData({
        name: user.name || "",
        email: user.email || "",
        phoneNumber: user.phoneNumber || user.phone || "",
        studentId: user.studentId || "",
        address: {
          street: user.address?.street || "",
          city: user.address?.city || "",
          state: user.address?.state || "",
          pincode: user.address?.pincode || "",
          country: user.address?.country || "India",
        },
        emergencyContact: {
          name: user.emergencyContact?.name || "",
          phone: user.emergencyContact?.phone || "",
          relation: user.emergencyContact?.relation || "",
        },
        aadharNumber: user.aadharNumber || "",
      });
    }
  }, [user]);

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
    if (newValue === 2 && !qrCodeData) {
      fetchQrCode();
    }
  };

  const fetchQrCode = async () => {
    if (!user?.studentId) return;
    setQrLoading(true);
    try {
      const res = await gateAPI.getStudentQR(user.studentId);
      setQrCodeData(res.data.qrCode);
    } catch (error) {
      console.error("Failed to fetch QR code:", error);
    } finally {
      setQrLoading(false);
    }
  };

  const handleProfileChange = (e) => {
    const { name, value } = e.target;
    if (name.includes(".")) {
      const [parent, child] = name.split(".");
      setProfileData((prev) => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: value,
        },
      }));
    } else {
      setProfileData((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handlePasswordChange = (e) => {
    setPasswordData({ ...passwordData, [e.target.name]: e.target.value });
  };

  const handleProfileSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await usersAPI.updateUser(user.id || user._id, profileData);
      updateUser(res.data.user);
      setIsEditing(false);
      toast.success("Profile updated successfully");
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      return toast.error("New passwords do not match");
    }

    setLoading(true);
    try {
      await usersAPI.changePassword(user.id || user._id, {
        currentPassword: passwordData.currentPassword,
        newPassword: passwordData.newPassword,
      });
      setPasswordData({ currentPassword: "", newPassword: "", confirmPassword: "" });
      toast.success("Password changed successfully");
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setLoading(false);
    }
  };

  const handleAvatarUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith("image/")) {
      toast.error("Please select an image file");
      return;
    }

    // Validate file size (1.5MB max for base64 storage)
    if (file.size > 1.5 * 1024 * 1024) {
      toast.error("Image too large. Please use an image under 1.5MB.");
      return;
    }

    setLoading(true);
    try {
      // Convert file to base64 data URL
      const reader = new FileReader();
      const base64Promise = new Promise((resolve, reject) => {
        reader.onload = () => resolve(reader.result);
        reader.onerror = reject;
      });
      reader.readAsDataURL(file);
      const imageDataUrl = await base64Promise;

      const res = await usersAPI.uploadAvatar(user.id || user._id, { image: imageDataUrl });
      updateUser(res.data.user);
      toast.success("Profile picture updated");
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setLoading(false);
    }
  };

  // Helper to get profile picture URL from various formats
  const getProfilePicUrl = (userData) => {
    if (!userData?.profilePicture) return "/default-avatar.png";
    if (typeof userData.profilePicture === "string") return userData.profilePicture;
    if (userData.profilePicture?.url) return userData.profilePicture.url;
    return "/default-avatar.png";
  };

  if (!user) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 1000, mx: "auto", py: 4, px: 2 }}>
      <Typography variant="h4" fontWeight="700" gutterBottom>
        My Profile
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
        Manage your account settings and profile information
      </Typography>

      <Grid container spacing={4}>
        {/* Left Column - Profile Card */}
        <Grid item xs={12} md={4}>
          <Card sx={{ textAlign: "center", position: "relative" }}>
            <CardContent sx={{ py: 6 }}>
              <Box sx={{ position: "relative", display: "inline-block" }}>
                <Avatar
                  src={getProfilePicUrl(user)}
                  sx={{ width: 150, height: 150, mx: "auto", mb: 2, border: "4px solid white", boxShadow: 3 }}
                />
                <input
                  accept="image/*"
                  style={{ display: "none" }}
                  id="avatar-button"
                  type="file"
                  onChange={handleAvatarUpload}
                />
                <label htmlFor="avatar-button">
                  <IconButton
                    color="primary"
                    aria-label="upload picture"
                    component="span"
                    sx={{
                      position: "absolute",
                      bottom: 20,
                      right: 5,
                      bgcolor: "white",
                      boxShadow: 2,
                      "&:hover": { bgcolor: "#f5f5f5" },
                    }}
                  >
                    <PhotoCameraIcon />
                  </IconButton>
                </label>
              </Box>

              <Typography variant="h5" fontWeight="600">
                {user.name}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ textTransform: "capitalize", mb: 2 }}>
                {user.role}
              </Typography>

              <Divider sx={{ my: 2 }} />

              <Box sx={{ textAlign: "left" }}>
                <Box display="flex" alignItems="center" mb={1}>
                  <BadgeIcon fontSize="small" sx={{ mr: 1, color: "text.secondary" }} />
                  <Typography variant="body2">{user.studentId || "No User ID Assigned"}</Typography>
                </Box>
                <Box display="flex" alignItems="center" mb={1}>
                  <EmailIcon fontSize="small" sx={{ mr: 1, color: "text.secondary" }} />
                  <Typography variant="body2">{user.email}</Typography>
                </Box>
                <Box display="flex" alignItems="center">
                  <PhoneIcon fontSize="small" sx={{ mr: 1, color: "text.secondary" }} />
                  <Typography variant="body2">{user.phoneNumber || user.phone || "No phone added"}</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Right Column - Tabs & Content */}
        <Grid item xs={12} md={8}>
          <Paper sx={{ width: "100%", borderRadius: 2 }}>
            <Tabs
              value={activeTab}
              onChange={handleTabChange}
              indicatorColor="primary"
              textColor="primary"
              variant="fullWidth"
              sx={{ borderBottom: 1, borderColor: "divider" }}
            >
              <Tab label="Info" icon={<PersonIcon />} iconPosition="start" />
              <Tab label="Security" icon={<LockIcon />} iconPosition="start" />
              {user.role === "student" && <Tab label="Digital Key" icon={<QrCodeIcon />} iconPosition="start" />}
            </Tabs>

            <Box sx={{ p: 4 }}>
              {/* Tab 0: Personal Info */}
              {activeTab === 0 && (
                <Box>
                  <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                    <Typography variant="h6">Personal Information</Typography>
                    {!isEditing ? (
                      <Button startIcon={<EditIcon />} variant="outlined" onClick={() => setIsEditing(true)}>
                        Edit
                      </Button>
                    ) : (
                      <Button variant="text" onClick={() => setIsEditing(false)}>
                        Cancel
                      </Button>
                    )}
                  </Box>

                  <form onSubmit={handleProfileSubmit}>
                    <Grid container spacing={3}>
                      {/* Personal Info Section */}
                      <Grid item xs={12}>
                        <Typography variant="subtitle2" color="primary" gutterBottom>
                          PERSONAL INFORMATION
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          label="Full Name"
                          name="name"
                          value={profileData.name}
                          onChange={handleProfileChange}
                          disabled={!isEditing}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          label="Email Address"
                          name="email"
                          value={profileData.email}
                          disabled={true}
                          helperText="Email cannot be changed"
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          label="Phone Number"
                          name="phoneNumber"
                          value={profileData.phoneNumber}
                          onChange={handleProfileChange}
                          disabled={!isEditing}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          label="User ID"
                          name="studentId"
                          value={profileData.studentId}
                          disabled={true}
                        />
                      </Grid>

                      {/* Contact Details Section */}
                      <Grid item xs={12}>
                        <Typography variant="subtitle2" color="primary" gutterBottom sx={{ mt: 2 }}>
                          CONTACT DETAILS
                        </Typography>
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          label="Street Address"
                          name="address.street"
                          value={profileData.address.street}
                          onChange={handleProfileChange}
                          disabled={!isEditing}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          label="City"
                          name="address.city"
                          value={profileData.address.city}
                          onChange={handleProfileChange}
                          disabled={!isEditing}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          label="State"
                          name="address.state"
                          value={profileData.address.state}
                          onChange={handleProfileChange}
                          disabled={!isEditing}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          label="Pincode"
                          name="address.pincode"
                          value={profileData.address.pincode}
                          onChange={handleProfileChange}
                          disabled={!isEditing}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          label="Country"
                          name="address.country"
                          value={profileData.address.country}
                          disabled={true}
                        />
                      </Grid>

                      {/* Emergency Contact Section */}
                      <Grid item xs={12}>
                        <Typography variant="subtitle2" color="primary" gutterBottom sx={{ mt: 2 }}>
                          EMERGENCY CONTACT
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <TextField
                          fullWidth
                          label="Contact Name"
                          name="emergencyContact.name"
                          value={profileData.emergencyContact.name}
                          onChange={handleProfileChange}
                          disabled={!isEditing}
                        />
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <TextField
                          fullWidth
                          label="Contact Phone"
                          name="emergencyContact.phone"
                          value={profileData.emergencyContact.phone}
                          onChange={handleProfileChange}
                          disabled={!isEditing}
                        />
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <TextField
                          fullWidth
                          label="Relation"
                          name="emergencyContact.relation"
                          value={profileData.emergencyContact.relation}
                          onChange={handleProfileChange}
                          disabled={!isEditing}
                          select={isEditing}
                          SelectProps={{ native: true }}
                        >
                          {!isEditing && <option value={profileData.emergencyContact.relation}>{profileData.emergencyContact.relation}</option>}
                          <option value="parent">Parent</option>
                          <option value="guardian">Guardian</option>
                          <option value="sibling">Sibling</option>
                          <option value="relative">Relative</option>
                          <option value="friend">Friend</option>
                          <option value="other">Other</option>
                        </TextField>
                      </Grid>

                      <Grid item xs={12}>
                        <Typography variant="subtitle2" color="primary" gutterBottom sx={{ mt: 2 }}>
                          GOVERNMENT IDENTIFICATION
                        </Typography>
                        <TextField
                          fullWidth
                          label="Aadhar Number"
                          name="aadharNumber"
                          value={profileData.aadharNumber}
                          onChange={handleProfileChange}
                          disabled={!isEditing}
                          placeholder="XXXX XXXX XXXX"
                          inputProps={{ maxLength: 14 }}
                        />
                      </Grid>

                      {isEditing && (
                        <Grid item xs={12}>
                          <Box display="flex" justifyContent="flex-end" sx={{ mt: 2 }}>
                            <Button
                              type="submit"
                              variant="contained"
                              startIcon={<SaveIcon />}
                              disabled={loading}
                              size="large"
                            >
                              {loading ? "Saving..." : "Save All Changes"}
                            </Button>
                          </Box>
                        </Grid>
                      )}
                    </Grid>

                    {user.role === "student" && user.room && (
                      <Box sx={{ mt: 5 }}>
                        <Typography variant="h6" gutterBottom>
                          Stay Information
                        </Typography>
                        <Divider sx={{ mb: 2 }} />
                        <Grid container spacing={2}>
                          <Grid item xs={12} sm={4}>
                            <Paper variant="outlined" sx={{ p: 2, textAlign: "center" }}>
                              <Typography variant="caption" color="text.secondary">Room Number</Typography>
                              <Typography variant="h6">{user.room.roomNumber}</Typography>
                            </Paper>
                          </Grid>
                          <Grid item xs={12} sm={4}>
                            <Paper variant="outlined" sx={{ p: 2, textAlign: "center" }}>
                              <Typography variant="caption" color="text.secondary">Room Type</Typography>
                              <Typography variant="h6" sx={{ textTransform: "capitalize" }}>{user.room.roomType || "Standard"}</Typography>
                            </Paper>
                          </Grid>
                          <Grid item xs={12} sm={4}>
                            <Paper variant="outlined" sx={{ p: 2, textAlign: "center" }}>
                              <Typography variant="caption" color="text.secondary">Status</Typography>
                              <Typography variant="h6" color="success.main">Active</Typography>
                            </Paper>
                          </Grid>
                        </Grid>
                      </Box>
                    )}
                  </form>
                </Box>
              )}

              {/* Tab 1: Security */}
              {activeTab === 1 && (
                <Box>
                  <Typography variant="h6" gutterBottom>
                    Change Password
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                    Ensure your account is using a long, random password to stay secure.
                  </Typography>

                  <form onSubmit={handlePasswordSubmit}>
                    <Grid container spacing={3} maxWidth={500}>
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          type="password"
                          label="Current Password"
                          name="currentPassword"
                          value={passwordData.currentPassword}
                          onChange={handlePasswordChange}
                          required
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          type="password"
                          label="New Password"
                          name="newPassword"
                          value={passwordData.newPassword}
                          onChange={handlePasswordChange}
                          required
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          type="password"
                          label="Confirm New Password"
                          name="confirmPassword"
                          value={passwordData.confirmPassword}
                          onChange={handlePasswordChange}
                          required
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <Button
                          type="submit"
                          variant="contained"
                          color="primary"
                          disabled={loading}
                        >
                          {loading ? "Processing..." : "Update Password"}
                        </Button>
                      </Grid>
                    </Grid>
                  </form>
                </Box>
              )}

              {/* Tab 2: Digital Key (QR) */}
              {activeTab === 2 && user.role === "student" && (
                <Box sx={{ textAlign: "center", py: 2 }}>
                  <Typography variant="h6" gutterBottom>
                    Your Digital Access Key
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
                    Use this QR code to scan at entry/exit gates. This code is unique to you.
                  </Typography>

                  <Box
                    sx={{
                      p: 4,
                      bgcolor: "#f9f9f9",
                      borderRadius: 4,
                      display: "inline-block",
                      border: "2px dashed #ddd",
                    }}
                  >
                    {qrLoading ? (
                      <CircularProgress size={100} sx={{ m: 4 }} />
                    ) : qrCodeData ? (
                      <Box>
                        <img
                          src={qrCodeData}
                          alt="Student QR Code"
                          style={{ width: 250, height: 250, borderRadius: 8 }}
                        />
                        <Typography variant="caption" display="block" sx={{ mt: 2, color: "text.secondary" }}>
                          Student ID: {user.studentId}
                        </Typography>
                      </Box>
                    ) : (
                      <Typography color="error">Failed to generate QR code. Please contact admin.</Typography>
                    )}
                  </Box>

                  <Box sx={{ mt: 4 }}>
                    <Button
                      variant="outlined"
                      startIcon={<QrCodeIcon />}
                      onClick={fetchQrCode}
                      disabled={qrLoading}
                    >
                      Refresh Key
                    </Button>
                  </Box>
                </Box>
              )}
            </Box>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default Profile;

